import { supabase } from '../../lib/supabase'
import { todayIso } from '../reports/reportDates'

// muster.shift_swap_state (supabase/migrations/20260929060000_shift_swaps.sql).
export type SwapState = 'proposed' | 'accepted' | 'declined' | 'approved' | 'denied'

export type ShiftOnDate = { date: string; label: string }

export type TeammateShift = ShiftOnDate & { employeeId: string; name: string }

export type MySwap = {
  id: string
  // Whether the agent proposed it or was asked.
  role: 'requester' | 'counterpart'
  state: SwapState
  otherName: string
  // The shift this agent gives up, and the one they would take.
  giveDate: string
  giveShift: string
  takeDate: string
  takeShift: string
  message?: string
  decisionComment?: string
}

export type SwapResult = { ok: true } | { ok: false; message: string }

const FAILED = 'The change could not be saved. Try again.'
const PROPOSE_REFUSED =
  'That swap cannot be proposed. Both shifts must be on published pay periods, and neither of you can already work the day you would take.'

async function getMyEmployeeId(userId: string): Promise<string | null> {
  const { data } = await supabase.from('employees').select('id').eq('profile_id', userId).maybeSingle()
  return data?.id ?? null
}

type EntryRow = {
  date: string
  start_time: string | null
  end_time: string | null
  status_codes: { name: string; category: string } | null
  assignments: { name: string } | null
}

// The work shifts the agent holds from today on, in the live schedule.
export async function getMyWorkShifts(userId: string): Promise<ShiftOnDate[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('schedule_entries')
    .select('date, start_time, end_time, status_codes(name, category), assignments(name)')
    .eq('employee_id', employeeId)
    .gte('date', todayIso())
    .order('date', { ascending: true })
  if (error || !data) return []
  return (data as unknown as EntryRow[])
    .filter((row) => row.status_codes?.category === 'work')
    .map((row) => ({
      date: row.date,
      label:
        (row.start_time && row.end_time ? `${row.start_time.slice(0, 5)} to ${row.end_time.slice(0, 5)}` : (row.status_codes?.name ?? 'Work')) +
        (row.assignments?.name ? `, ${row.assignments.name}` : ''),
    }))
}

type VersionRow = {
  team_id: string
  period_start: string
  version: number
  entries: { employeeId?: string; employeeName: string; date: string; category: string; label: string; detail?: string }[]
}

// The work shifts of the agent's teammates from today on, from the latest
// published version of each pay period. Row-level security limits the versions
// to the agent's own team.
export async function getTeammateShifts(userId: string): Promise<TeammateShift[]> {
  const employeeId = await getMyEmployeeId(userId)
  const { data, error } = await supabase
    .from('schedule_versions')
    .select('team_id, period_start, version, entries')
    .gte('period_end', todayIso())
    .order('version', { ascending: false })
  if (error || !data) return []

  const latest = new Map<string, VersionRow>()
  for (const row of data as unknown as VersionRow[]) {
    const key = `${row.team_id}|${row.period_start}`
    if (!latest.has(key)) latest.set(key, row)
  }

  const today = todayIso()
  return Array.from(latest.values())
    .flatMap((version) => version.entries)
    .filter((entry) => entry.category === 'work' && entry.employeeId && entry.employeeId !== employeeId && entry.date >= today)
    .map((entry) => ({
      employeeId: entry.employeeId!,
      name: entry.employeeName,
      date: entry.date,
      label: entry.label + (entry.detail ? `, ${entry.detail}` : ''),
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.date.localeCompare(b.date))
}

type SwapRow = {
  id: string
  requester_employee_id: string
  requester_date: string
  counterpart_date: string
  requester_name: string | null
  counterpart_name: string | null
  requester_shift: string | null
  counterpart_shift: string | null
  message: string | null
  state: SwapState
  decisions: { comment: string | null; decided_at: string }[] | null
}

// The swaps the agent proposed or was asked into, newest first.
export async function getMySwaps(userId: string): Promise<MySwap[]> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return []
  const { data, error } = await supabase
    .from('shift_swaps')
    .select(
      'id, requester_employee_id, requester_date, counterpart_date, requester_name, counterpart_name, requester_shift, counterpart_shift, message, state, decisions(comment, decided_at)',
    )
    .order('requester_date', { ascending: false })
  if (error || !data) return []

  return (data as unknown as SwapRow[]).map((row) => {
    const mine = row.requester_employee_id === employeeId
    const decision = (row.decisions ?? []).reduce<{ comment: string | null; decided_at: string } | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    return {
      id: row.id,
      role: mine ? 'requester' : 'counterpart',
      state: row.state,
      otherName: (mine ? row.counterpart_name : row.requester_name) ?? 'A colleague',
      giveDate: mine ? row.requester_date : row.counterpart_date,
      giveShift: (mine ? row.requester_shift : row.counterpart_shift) ?? '',
      takeDate: mine ? row.counterpart_date : row.requester_date,
      takeShift: (mine ? row.counterpart_shift : row.requester_shift) ?? '',
      message: row.message ?? undefined,
      decisionComment: decision?.comment ?? undefined,
    }
  })
}

export type NewSwap = {
  requesterDate: string
  counterpartEmployeeId: string
  counterpartDate: string
  message?: string
}

// Proposes a trade, awaiting the colleague. Row-level security holds the rules:
// both shifts are work, on published periods, in the future, on this team, and
// neither agent already works the day they would take.
export async function proposeSwap(userId: string, swap: NewSwap): Promise<SwapResult> {
  const employeeId = await getMyEmployeeId(userId)
  if (!employeeId) return { ok: false, message: FAILED }
  const today = todayIso()
  const { data: placement } = await supabase
    .from('employee_assignments')
    .select('team_id')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
    .maybeSingle()
  if (!placement) return { ok: false, message: FAILED }

  const { error } = await supabase.from('shift_swaps').insert({
    team_id: placement.team_id,
    requester_employee_id: employeeId,
    requester_date: swap.requesterDate,
    counterpart_employee_id: swap.counterpartEmployeeId,
    counterpart_date: swap.counterpartDate,
    message: swap.message?.trim() ? swap.message.trim() : null,
    state: 'proposed',
    created_by: userId,
  })
  if (!error) return { ok: true }
  return { ok: false, message: error.code === '42501' ? PROPOSE_REFUSED : FAILED }
}

// The colleague accepts or declines a proposal made to them.
export async function answerSwap(userId: string, id: string, accept: boolean): Promise<SwapResult> {
  const { error } = await supabase
    .from('shift_swaps')
    .update({ state: accept ? 'accepted' : 'declined', counterpart_decided_at: new Date().toISOString(), updated_by: userId })
    .eq('id', id)
  return error ? { ok: false, message: FAILED } : { ok: true }
}

// Withdraws a proposal the supervisor has not yet decided.
export async function withdrawSwap(id: string): Promise<SwapResult> {
  const { error } = await supabase.from('shift_swaps').delete().eq('id', id)
  return error ? { ok: false, message: FAILED } : { ok: true }
}
